import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  DisclosureJurisdictionView,
  DocumentRequirementView,
  EligibilityEvaluationListResponse,
  JurisdictionListResponse,
  JurisdictionStatus,
  MoneyView,
  PartnerListResponse,
  PartnerView,
  ProgramRuleView,
  ProgramVersionListResponse,
  ProgramVersionView,
  SubmissionMethod,
} from '@tex/finance-shared'
import { apiGet, apiSend } from './api'

const partnersBase = '/api/v1/partners'
const programsBase = '/api/v1/programs'
const IDEMPOTENCY_HEADER = 'Idempotency-Key'

export const programKeys = {
  partners: ['partners'] as const,
  partner: (id: string) => ['partners', id] as const,
  partnerPrograms: (id: string) => ['partners', id, 'programs'] as const,
  program: (id: string) => ['programs', id] as const,
  jurisdictions: ['jurisdictions'] as const,
  disclosure: (state: string) => ['disclosure-jurisdictions', state] as const,
  evaluations: (caseId: string) => ['cases', caseId, 'eligibility-evaluations'] as const,
}

/** Request bodies mirror the zod schemas in apps/api/src/routes/programs.ts. */
export interface CreatePartnerInput {
  code: string
  legal_name: string
  display_name: string
  is_synthetic: boolean
  submission_method: SubmissionMethod
  submission_instructions?: string | null
}

export interface PatchPartnerInput {
  legal_name?: string
  display_name?: string
  submission_instructions?: string | null
  agreement_reference?: string | null
  agreement_effective_from?: string | null
  agreement_effective_to?: string | null
}

export type PartnerCommandInput =
  | { command: 'activate'; agreement_reference: string; agreement_effective_from: string; access_verified_at: string }
  | { command: 'suspend'; reason: string }
  | { command: 'terminate'; reason: string }

export interface ProgramDefinitionInput {
  title?: string
  rules?: ProgramRuleView[]
  document_requirements?: DocumentRequirementView[]
  unknown_policy?: 'manual_review' | 'auto_decline'
  effective_from?: string | null
  effective_to?: string | null
}

export type CreateProgramInput = ProgramDefinitionInput & { program_code: string; title: string }

export type ProgramCommandInput =
  | { command: 'mark_test_only'; effective_from?: string }
  | { command: 'activate'; criteria_verified_at: string; effective_from?: string }
  | { command: 'retire' }

export interface PutJurisdictionInput {
  status: JurisdictionStatus
  basis: string
  effective_from?: string | null
  effective_to?: string | null
}

export interface PutDisclosureInput {
  law_reference: string
  threshold_amount?: MoneyView | null
  broker_duty_note?: string | null
  verified: boolean
}

export function usePartners() {
  return useQuery({ queryKey: programKeys.partners, queryFn: () => apiGet<PartnerListResponse>(partnersBase) })
}

export function usePartner(id: string) {
  return useQuery({ queryKey: programKeys.partner(id), queryFn: () => apiGet<{ partner: PartnerView }>(`${partnersBase}/${id}`) })
}

export function usePartnerPrograms(partnerId: string) {
  return useQuery({
    queryKey: programKeys.partnerPrograms(partnerId),
    queryFn: () => apiGet<ProgramVersionListResponse>(`${partnersBase}/${partnerId}/programs`),
  })
}

export function useProgram(id: string) {
  return useQuery({ queryKey: programKeys.program(id), queryFn: () => apiGet<{ program: ProgramVersionView }>(`${programsBase}/${id}`) })
}

export function useJurisdictions() {
  return useQuery({ queryKey: programKeys.jurisdictions, queryFn: () => apiGet<JurisdictionListResponse>('/api/v1/jurisdictions') })
}

export function useDisclosureJurisdiction(state: string | null) {
  return useQuery({
    queryKey: programKeys.disclosure(state ?? ''),
    queryFn: () => apiGet<{ disclosure_jurisdiction: DisclosureJurisdictionView }>(`/api/v1/disclosure-jurisdictions/${state}`),
    enabled: state !== null,
  })
}

export function useEligibilityEvaluations(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: programKeys.evaluations(caseId),
    queryFn: () => apiGet<EligibilityEvaluationListResponse>(`/api/v1/cases/${caseId}/eligibility-evaluations`),
    enabled,
  })
}

function useInvalidating<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>, keys: readonly (readonly string[])[]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })))
    },
  })
}

export function useCreatePartner() {
  return useInvalidating((input: CreatePartnerInput) => apiSend<{ partner: PartnerView }>('POST', partnersBase, input), [programKeys.partners])
}

export function usePatchPartner(partnerId: string) {
  return useInvalidating(
    (input: PatchPartnerInput) => apiSend<{ partner: PartnerView }>('PATCH', `${partnersBase}/${partnerId}`, input),
    [programKeys.partners],
  )
}

export function usePartnerCommand(partnerId: string) {
  return useInvalidating(
    (input: PartnerCommandInput) => apiSend<{ partner: PartnerView }>('POST', `${partnersBase}/${partnerId}/status`, input),
    [programKeys.partners],
  )
}

export function useCreateProgram(partnerId: string) {
  return useInvalidating(
    (input: CreateProgramInput) => apiSend<{ program: ProgramVersionView }>('POST', `${partnersBase}/${partnerId}/programs`, input),
    [programKeys.partnerPrograms(partnerId), programKeys.partners],
  )
}

export function usePatchProgram(program: Pick<ProgramVersionView, 'id' | 'partner_id'>) {
  return useInvalidating(
    (input: ProgramDefinitionInput) => apiSend<{ program: ProgramVersionView }>('PATCH', `${programsBase}/${program.id}`, input),
    [programKeys.program(program.id), programKeys.partnerPrograms(program.partner_id)],
  )
}

export function useNewProgramVersion(program: Pick<ProgramVersionView, 'id' | 'partner_id'>) {
  return useInvalidating(
    (input: ProgramDefinitionInput = {}) => apiSend<{ program: ProgramVersionView }>('POST', `${programsBase}/${program.id}/new-version`, input),
    [programKeys.partnerPrograms(program.partner_id)],
  )
}

export function useProgramCommand(program: Pick<ProgramVersionView, 'id' | 'partner_id'>) {
  return useInvalidating(
    (input: ProgramCommandInput) => apiSend<{ program: ProgramVersionView }>('POST', `${programsBase}/${program.id}/status`, input),
    [programKeys.program(program.id), programKeys.partnerPrograms(program.partner_id), ['programs'] as const],
  )
}

export function usePutJurisdiction() {
  return useInvalidating(
    ({ state, ...input }: PutJurisdictionInput & { state: string }) =>
      apiSend<{ jurisdiction: JurisdictionListResponse['jurisdictions'][number] }>('PUT', `/api/v1/jurisdictions/${state}`, input),
    [programKeys.jurisdictions],
  )
}

export function usePutDisclosureJurisdiction(state: string) {
  return useInvalidating(
    (input: PutDisclosureInput) =>
      apiSend<{ disclosure_jurisdiction: DisclosureJurisdictionView }>('PUT', `/api/v1/disclosure-jurisdictions/${state}`, input),
    [programKeys.disclosure(state)],
  )
}

export function useEvaluateEligibility(caseId: string) {
  return useInvalidating(
    () =>
      apiSend<EligibilityEvaluationListResponse>('POST', `/api/v1/cases/${caseId}/eligibility-evaluations`, undefined, {
        [IDEMPOTENCY_HEADER]: crypto.randomUUID(),
      }),
    [programKeys.evaluations(caseId)],
  )
}
